import React, { useEffect, useState } from 'react';

import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { CONFIGS } from '../../utilities/Configs';
import { ConstructFetchBody, FormatDate, UrlQueryParam } from '../../utilities/Helpers';
import { TowerPhotoRequestBrandSearchResult, TowerPhotoRequestDistributorSearchResult, TowerPhotoRequest } from '../../utilities/Types';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';

import { BrandAdd } from './BrandAdd';
import { Photo } from './Photo';

type Props = {
	towerPhotoRequest: TowerPhotoRequest,
	position: number,
	onApprove: (position: number, faucet: TowerPhotoRequest['DetectedFaucets'][number]) => void,
	onReject: (position: number) => void,
	onClose: () => void
};

export const FaucetEdit = (props: Props) => {
	const currentFaucet = props.towerPhotoRequest['CurrentFaucets'][props.position];
	const detectedFaucet = props.towerPhotoRequest['DetectedFaucets'][props.position];

	const [brands, setBrands] = useState<Array<TowerPhotoRequestBrandSearchResult>>([]);
	const [distributors, setDistributors] = useState<Array<TowerPhotoRequestDistributorSearchResult>>([]);
	const [isBrandAddDialogOpen, setIsBrandAddDialogOpen] = useState<boolean>(false);

	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		if (detectedFaucet['bid'] !== null && detectedFaucet['brand_name'] !== null) {
			setBrands([
				{
					'bid': detectedFaucet['bid'],
					'brand_name': detectedFaucet['brand_name'],
					'did': detectedFaucet['did'],
					'distributor_name': detectedFaucet['distributor_name']
				}
			]);
		}

		if (detectedFaucet['did'] !== null && detectedFaucet['distributor_name'] !== null) {
			setDistributors([
				{
					'did': detectedFaucet['did'],
					'distributor_name': detectedFaucet['distributor_name']
				}
			]);
		}
	}, []);

	const handleChangeQueryBrand = (_query) => {
		if (_query.length >= 2) {
			fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.BRAND_SEARCH_LIST_GET, [UrlQueryParam('token'), UrlQueryParam('serviceCenterId'), _query, 50, 'name', 'ASC'])).then(
				(_response) => _response.json()
			).then(
				(_response) => setBrands(_response['result'])
			).catch(
				() => enqueueSnackbar('An unexpected error has occurred. Please try again later. If problem persists, please contact support.', { variant: 'error' })
			);
		}
	};

	const handleChangeQueryDistributor = (_query) => {
		if (_query.length >= 2) {
			fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.DISTRIBUTOR_SEARCH_LIST_GET, [UrlQueryParam('token'), UrlQueryParam('serviceCenterId'), _query, 50, 'name', 'ASC'])).then(
				(_response) => _response.json()
			).then(
				(_response) => setDistributors(_response['result'])
			).catch(
				() => enqueueSnackbar('An unexpected error has occurred. Please try again later. If problem persists, please contact support.', { variant: 'error' })
			);
		}
	};

	const handleSubmit = (_values, _formikBag) => {
		props.onApprove(
			props.position,
			{
				'flid': detectedFaucet['flid'],
				'bid': _values.brand.id,
				'brand_name': _values.brand.name,
				'did': _values.distributor.id,
				'distributor_name': _values.distributor.name,
				'date_requested': detectedFaucet['date_requested']
			}
		);

		props.onClose();
	};

	const handleReject = () => {
		props.onReject(props.position);
		props.onClose();
	};
	
	return (
		<Formik
			initialValues={{
				brand: {
					id: (detectedFaucet['bid'] || ''),
					name: ''
				},
				distributor: {
					id: (detectedFaucet['did'] || ''),
					name: ''
				}
			}}
			onSubmit={handleSubmit}
			validateOnBlur={false}
			validateOnChange={false}
			validationSchema={
				Yup.object().shape({
					brand: Yup.object().shape({
						id: Yup.number().required('Required'),
						name: Yup.string()
					}),
					distributor: Yup.object().shape({
						id: Yup.number().required('Required'),
						name: Yup.string()
					})
				})
			}
		>
			{
				(_formik) => (
					<Dialog open maxWidth="md" fullWidth>
						<DialogTitle className="header orange">Edit Faucet</DialogTitle>
						<DialogContent dividers>
							<Grid container spacing={1}>
								<Grid item xs={12} md={3}>
									<div className="column">
										<p className="bold">Old Values</p>
										{
											currentFaucet === undefined ? (
												<p className="mute">No faucet found</p>
											) : (
												<>
													<div>
														<p className="small mute">Brand</p>
														{
															(currentFaucet['brand_name'] || '').trim() === '' ? (
																<p className="mute">No brand</p>
															) : (
																<p>{currentFaucet['brand_name']}</p>
															)
														}
													</div>
													<div>
														<p className="small mute">Distributor</p>
														{
															(currentFaucet['dist_name'] || '').trim() === '' ? (
																<p className="mute">No distributor</p>
															) : (
																<p>{currentFaucet['dist_name']}</p>
															)
														}
													</div>
												</>
											)
										}
									</div>
								</Grid>
								<Grid item xs={12} md={3}>
									<div className="column">
										<p className="bold">New Values</p>
										<div>
											<p className="small mute">Brand (AI Suggestion)</p>
											{
												(detectedFaucet['brand_name'] || '').trim() === '' ? (
													<p className="mute">No match</p>
												) : (
													<p>{detectedFaucet['brand_name']}</p>
												)
											}
										</div>
										<div>
											<Autocomplete
												options={brands}
												filterOptions={createFilterOptions({
													limit: 50,
													trim: true
												})}
												value={brands.find((_brand) => (_brand['bid'] === _formik.values.brand.id)) || null}
												getOptionLabel={(_brand) => _brand['brand_name']}
												renderInput={
													(_params) => (
														<TextField
															{..._params}
															label="Brand"
															placeholder={(brands.length > 50 ? 'Refine search, top ' + 50 + ' results shown...' : 'Select...')}
															autoFocus={true}
															InputLabelProps={{
																shrink: true
															}}
															variant="outlined"
															margin="dense"
															onChange={(_event) => handleChangeQueryBrand(_event.target.value)}
														/>
													)
												}
												onChange={
													(_event, _brand) => {
														if (_brand !== null) {
															_formik.setFieldValue('brand', { id: _brand['bid'], name: _brand['brand_name'] });
															
															if (_brand['did'] !== null && _brand['distributor_name'] !== null) {
																setDistributors([
																	{
																		'did': _brand['did'],
																		'distributor_name': _brand['distributor_name']
																	}
																]);

																_formik.setFieldValue('distributor', { id: _brand['did'], name: _brand['distributor_name'] });
															}
														}
													}
												}
											/>
											{
												_formik.errors.brand !== undefined && (
													_formik.errors.brand.id !== undefined && (
														<p className="error">{_formik.errors.brand.id}</p>
													)
												)
											}
										</div>
										<a className="end pointer" style={{ color: '#F1BA50' }} onClick={() => setIsBrandAddDialogOpen(true)}>Create a New Brand</a>
										<div>
											<p className="small mute">Distributor (AI Suggestion)</p>
											{
												(detectedFaucet['distributor_name'] || '').trim() === '' ? (
													<p className="mute">No match</p>
												) : (
													<p>{detectedFaucet['distributor_name']}</p>
												)
											}
										</div>
										<div>
											<Autocomplete
												options={distributors}
												filterOptions={createFilterOptions({
													limit: 50,
													trim: true
												})}
												value={distributors.find((_distributor) => (_distributor['did'] === _formik.values.distributor.id)) || null}
												getOptionLabel={(_distributor) => _distributor['distributor_name']}
												renderInput={
													(_params) => (
														<TextField
															{..._params}
															label="Distributor"
															placeholder={(distributors.length > 50 ? 'Refine search, top ' + 50 + ' results shown...' : 'Select...')}
															InputLabelProps={{
																shrink: true
															}}
															variant="outlined"
															margin="dense"
															onChange={(_event) => handleChangeQueryDistributor(_event.target.value)}
														/>
													)
												}
												onChange={(_event, _distributor) => (_distributor !== null ? _formik.setFieldValue('distributor', { id: _distributor['did'], name: _distributor['distributor_name'] }) : () => {})}
											/>
											{
												_formik.errors.distributor !== undefined && (
													_formik.errors.distributor.id !== undefined && (
														<p className="error">{_formik.errors.distributor.id}</p>
													)
												)
											}
										</div>
										{
											detectedFaucet['date_requested'] !== null && (
												<div>
													<p className="small mute">Date Requested</p>
													<p>{FormatDate(new Date(detectedFaucet['date_requested']))}</p>
												</div>
											)
										}
									</div>
								</Grid>
								<Grid item xs={12} md={6}>
									<Photo
										urlOriginal={CONFIGS.STORAGE.IMAGE_TOWER_URL.ORIGINAL.replace('{ID}', props.towerPhotoRequest['Location']['Table'][0]['req_guid'])}
										urlEdited={props.towerPhotoRequest['Location']['Table'][0]['has_cropped'] === true ? CONFIGS.STORAGE.IMAGE_TOWER_URL.EDITED.replace('{ID}', props.towerPhotoRequest['Location']['Table'][0]['req_guid']) : undefined}
										expandable={false}
									/>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions>
							<div className="row stretch spread">
								<div className="stretch">
									<Button variant="contained" disabled={_formik.isSubmitting} className="red" onClick={handleReject}>Reject</Button>
								</div>
								<Button variant="contained" color="default" disabled={_formik.isSubmitting} onClick={props.onClose}>Cancel</Button>
								<Button variant="contained" disabled={_formik.isSubmitting} className="green" onClick={() => _formik.handleSubmit()}>Update</Button>
							</div>
						</DialogActions>
						{
							isBrandAddDialogOpen === true && (
								<BrandAdd onAdd={() => {}} onClose={() => setIsBrandAddDialogOpen(false)} />
							)
						}
					</Dialog>
				)
			}
		</Formik>
	);
};